<template>
  <div>
    <div class="hamburger-menu" @click="toggleMobileSidebar">
      <img src="https://img.icons8.com/material-outlined/24/000000/menu--v1.png" alt="Menu">
    </div>
    <div class="sidebar" :class="{ 'mobile-active': isMobileActive, 'mobile-collapsed': isMobileCollapsed }">
      <div class="sidebar-header">
        <img v-if="!isMobileCollapsed || isDesktop" src="@/assets/logo.png" alt="Stark Shopper" class="logo">
      </div>
      <!-- Add a scrollable container for the menu items -->
      <div class="sidebar-content">
        <ul class="sidebar-menu">
          <li v-for="(item, itemIndex) in filteredMenuItems" :key="`menu-${itemIndex}`" class="menu-item">
            <router-link 
              v-if="!item.submenu" 
              :to="item.link" 
              @click="activateMenuItem(itemIndex)"
              :class="{'active': $route.path === item.link}"
            >
              <img :src="item.icon" alt="Icon" class="menu-icon">
              <span class="menu-text">{{ item.name }}</span>
            </router-link>
            <div v-else class="menu-container">
              <a @click="toggleSubmenu(itemIndex)" :class="{'active': isSubMenuActive(item) || activeStates[itemIndex]}">
                <img :src="item.icon" alt="Icon" class="menu-icon">
                <span class="menu-text">{{ item.name }}</span>
                <img 
                  src="https://img.icons8.com/material-outlined/24/000000/expand-arrow--v1.png" 
                  alt="Expand"
                  class="submenu-indicator"
                  :class="{'rotate': activeStates[itemIndex]}"
                >
              </a>
              <ul v-if="item.submenu && activeStates[itemIndex]" class="submenu">
                <li v-for="(subItem, subIndex) in item.submenu" :key="`submenu-${itemIndex}-${subIndex}`">
                  <template v-if="!subItem.submenu">
                    <router-link 
                      :to="subItem.link" 
                      @click="activateSubMenuItem(itemIndex, subIndex)"
                      :class="{'active': $route.path === subItem.link}"
                    >
                      {{ subItem.name }}
                    </router-link>
                  </template>
                  <template v-else>
                    <a @click="toggleNestedSubmenu(itemIndex, subIndex)" :class="{'active': isNestedSubMenuActive(subItem)}">
                      {{ subItem.name }}
                      <img 
                        src="https://img.icons8.com/material-outlined/24/000000/expand-arrow--v1.png" 
                        alt="Expand"
                        class="nested-submenu-indicator"
                        :class="{'rotate': subActiveStates[`${itemIndex}-${subIndex}`] || isNestedSubMenuActive(subItem)}"
                      >
                    </a>
                    <ul v-if="subActiveStates[`${itemIndex}-${subIndex}`] || isNestedSubMenuActive(subItem)" class="nested-submenu">
                      <li v-for="(nestedItem, nestedIndex) in subItem.submenu" :key="`nested-${itemIndex}-${subIndex}-${nestedIndex}`">
                        <router-link 
                          :to="nestedItem.link" 
                          @click="activateNestedSubMenuItem(itemIndex, subIndex, nestedIndex)"
                          :class="{'active': $route.path === nestedItem.link}"
                        >
                          {{ nestedItem.name }}
                        </router-link>
                      </li>
                    </ul>
                  </template>
                </li>
              </ul>
            </div>
          </li>
        </ul>
      </div>
      <div class="version-number">Version 3.0.0</div>
    </div>
    <div class="overlay" v-if="isMobileActive" @click="closeMobileSidebar"></div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { authState } from '@/services/auth'; // Import authState to watch for changes

export default {
  name: 'SideBar',
  setup() {
    const router = useRouter();
    const route = useRoute();

    const user = ref(authState.user); // Use authState.user to watch for changes
    const isMobileActive = ref(false);
    const isMobileCollapsed = ref(true);
    const canViewUserData = ref(authState.canViewUserData); // Use authState.canViewUserData
    
    // Use separate reactive objects for active states
    const activeStates = reactive({});
    const subActiveStates = reactive({});

    // Define menu items without reactive status flags
    const baseMenuItems = [
      {
        name: 'Home',
        icon: 'https://img.icons8.com/material-outlined/24/000000/control-panel--v1.png',
        link: '/dashboard'
      },
      {
        name: 'Administration',
        icon: 'https://img.icons8.com/material-outlined/24/000000/lock--v1.png',
        submenu: [
          { 
            name: 'Business Management',
            submenu: [
              { name: 'Create Business', link: '/dashboard/administration/create-business', requiresAdmin: true },
              { name: 'Update Business', link: '/dashboard/administration/update-business', requiresAdmin: true }
            ],
            requiresAdmin: true
          },
          { 
            name: 'User Management',
            submenu: [
              { name: 'Create User', link: '/dashboard/administration/create-user', requiresAdmin: true },
              { name: 'Update User', link: '/dashboard/administration/update-user', requiresAdmin: true },
              { name: 'View Users', link: '/dashboard/administration/view-users', requiresAdmin: true }
            ],
            requiresAdmin: true
          },
          {
            name: 'Order Management',
            submenu: [
              { name: 'Sales', link: '/dashboard/administration/sales', requiresAdmin: true },
              { name: 'Payouts', link: '/dashboard/administration/payouts', requiresAdmin: true },
              { name: 'Search Orders', link: '/dashboard/administration/search-orders', requiresAdmin: true }
            ],
            requiresAdmin: true
          },
          { 
            name: 'Content Management',
            submenu: [
              { name: 'Email List', link: '/dashboard/administration/email-list', requiresAdmin: true },
              { name: 'Approve Reviews', link: '/dashboard/administration/approve-reviews', requiresAdmin: true },
              { name: 'Discount Code Usage', link: '/dashboard/view-discount-users', requiresAdmin: true }
            ],
            requiresAdmin: true
          }
        ],
        requiresAdmin: true
      },
      {
        name: 'Incentives',
        icon: 'https://img.icons8.com/material-outlined/24/000000/bill.png',
        submenu: [
          { name: 'View All Incentives', link: '/dashboard/view-incentives' },
          { name: 'Create Incentive', link: '/dashboard/create-incentive' },
          { 
            name: 'Admin Options',
            submenu: [
              { name: 'Create for Business', link: '/dashboard/create-incentive-for-business', requiresAdmin: true },
              { name: 'Remove for Business', link: '/dashboard/remove-incentive-for-business', requiresAdmin: true }
            ],
            requiresAdmin: true
          }
        ]
      },
      {
        name: 'Gift Cards',
        icon: 'https://img.icons8.com/material-outlined/24/000000/bank-card-back-side.png',
        submenu: [
          { name: 'View All Gift Cards', link: '/dashboard/view-gift-cards' },
          { name: 'Gift Card Sales Analytics', link: '/dashboard/gift-card-sales' },
          { name: 'View Gift Card Users', link: '/dashboard/view-gift-card-users', requiresPermission: true },
          { name: 'Shopify Management', link: '/dashboard/shopify-connect', requiresAdmin: true },
          {
            name: 'Admin Options',
            submenu: [
              { name: 'Create Gift Card', link: '/dashboard/create-gift-card', requiresAdmin: true },
              { name: 'Edit Gift Card', link: '/dashboard/edit-gift-card', requiresAdmin: true },
              { name: 'Remove Gift Cards', link: '/dashboard/remove-gift-card-for-business', requiresAdmin: true },
              { name: 'Gift Card Inventory', link: '/dashboard/admin/gift-card-inventory', requiresAdmin: true }
            ],
            requiresAdmin: true
          }
        ]
      },
      {
        name: 'Business Profile',
        icon: 'https://img.icons8.com/material-outlined/24/000000/user.png',
        link: '/dashboard/business-profile'
      },
      {
        name: 'Send Notifications',
        icon: 'https://img.icons8.com/material-outlined/24/000000/filled-appointment-reminders.png',
        link: '/dashboard/notification'
      },
      {
        name: 'Advertising',
        icon: 'https://img.icons8.com/material-outlined/24/000000/commercial--v1.png',
        submenu: [
          {
            name: 'Sponsored Content',
            submenu: [
              { name: 'View Sponsored Items', link: '/dashboard/view-sponsored-items', requiresAdmin: true },
              { name: 'Create Sponsored Item', link: '/dashboard/create-sponsored-item', requiresAdmin: true }
            ],
            requiresAdmin: true
          },
          {
            name: 'Visual Marketing',
            submenu: [
              { name: 'Create Slider Ad', link: '/dashboard/create-slider-ad', requiresAdmin: true },
              { name: 'View Slider Ads', link: '/dashboard/view-slider-ads', requiresAdmin: true },
              { name: 'View Banners', link: '/dashboard/view-banners', requiresAdmin: true }
            ],
            requiresAdmin: true
          },
          {
            name: 'Promotions',
            submenu: [
              { name: 'Create Trending', link: '/dashboard/create-trending', requiresAdmin: true },
              { name: 'Discounts', link: '/dashboard/view-discount', requiresAdmin: true }
            ],
            requiresAdmin: true
          }
        ],
        requiresAdmin: true
      },
      {
        name: 'Support',
        icon: 'https://img.icons8.com/material-outlined/24/000000/phone.png',
        link: '/support'
      },
      {
        name: 'Leave Dashboard',
        icon: 'https://img.icons8.com/material-outlined/24/000000/circled-left--v1.png',
        link: '/'
      }
    ];

    const isDesktop = computed(() => window.innerWidth >= 1024);

    // We'll store a separate deep copy of the filtered menu items
    const filteredMenuItems = computed(() => {
      const isAdmin = user.value?.isAdmin;
      const isBusiness = user.value?.isBusiness;
      
      // Start with a deep copy of the base items
      const menuItemsCopy = JSON.parse(JSON.stringify(baseMenuItems));
      
      return menuItemsCopy.map(item => {
        // Special handling for Home based on user roles
        if (item.name === 'Home') {
          if (isAdmin && isBusiness) {
            // Create a special version of Home for users with both roles
            return {
              name: 'Home',
              icon: 'https://img.icons8.com/material-outlined/24/000000/control-panel--v1.png',
              submenu: [
                { name: 'Admin Dashboard', link: '/dashboard/admin-dashboard' },
                { name: 'Business Dashboard', link: '/dashboard' }
              ]
            };
          } else if (isAdmin) {
            return { ...item, link: '/dashboard/admin-dashboard' };
          } else if (isBusiness) {
            return { ...item, link: '/dashboard' };
          }
        }

        // Filter submenu items by role and permission
        if (item.submenu) {
          item.submenu = filterSubmenuItemsByPermissions(item.submenu, isAdmin);
        }

        // Filter top-level items by role
        if (item.requiresAdmin) {
          return isAdmin ? item : undefined;
        }

        return item;
      }).filter(item => item !== undefined && (item.submenu ? item.submenu.length > 0 : true));
    });

    const filterSubmenuItemsByPermissions = (submenu, isAdmin) => {
      if (!submenu) return [];
      
      return submenu
        .map(subItem => {
          if (subItem.requiresAdmin && !isAdmin) {
            return undefined;
          }
          
          if (subItem.requiresPermission && !canViewUserData.value) {
            return undefined;
          }
          
          // If this item has a nested submenu, filter it too
          if (subItem.submenu) {
            const filteredNestedSubmenu = filterSubmenuItemsByPermissions(subItem.submenu, isAdmin);
            return filteredNestedSubmenu.length > 0 ? { ...subItem, submenu: filteredNestedSubmenu } : undefined;
          }
          
          return subItem;
        })
        .filter(item => item !== undefined);
    };

    // Utility functions that don't modify the reactive state directly
    const isSubMenuActive = (item) => {
      if (!item.submenu) return false;
      
      // Special handling for Home menu
      if (item.name === 'Home') {
        const dashboardPaths = ['/dashboard', '/dashboard/admin-dashboard'];
        if (dashboardPaths.includes(route.path)) {
          return true;
        }
      }
      
      return item.submenu.some(subItem => {
        if (subItem.submenu) {
          return isNestedSubMenuActive(subItem);
        }
        return route.path === subItem.link;
      });
    };

    const isNestedSubMenuActive = (subItem) => {
      if (!subItem.submenu) return false;
      
      return subItem.submenu.some(nestedItem => route.path === nestedItem.link);
    };

    // New action methods that update the activeStates instead of modifying the menu items
    const activateMenuItem = (itemIndex) => {
      // Reset all active states
      Object.keys(activeStates).forEach(key => {
        activeStates[key] = false;
      });
      
      const item = filteredMenuItems.value[itemIndex];
      if (item.link) {
        router.push(item.link);
      }
    };

    const activateSubMenuItem = (itemIndex, subIndex) => {
      // Reset all active states
      Object.keys(activeStates).forEach(key => {
        activeStates[key] = false;
      });
      
      // Reset all subactive states
      Object.keys(subActiveStates).forEach(key => {
        subActiveStates[key] = false;
      });
      
      // Set the parent as active
      activeStates[itemIndex] = true;
      
      const item = filteredMenuItems.value[itemIndex];
      const subItem = item.submenu[subIndex];
      
      if (subItem.link) {
        router.push(subItem.link);
      }
    };

    const activateNestedSubMenuItem = (itemIndex, subIndex, nestedIndex) => {
      // Reset all active states
      Object.keys(activeStates).forEach(key => {
        activeStates[key] = false;
      });
      
      // Reset all subactive states
      Object.keys(subActiveStates).forEach(key => {
        subActiveStates[key] = false;
      });
      
      // Set the parent and subparent as active
      activeStates[itemIndex] = true;
      subActiveStates[`${itemIndex}-${subIndex}`] = true;
      
      const item = filteredMenuItems.value[itemIndex];
      const subItem = item.submenu[subIndex];
      const nestedItem = subItem.submenu[nestedIndex];
      
      if (nestedItem.link) {
        router.push(nestedItem.link);
      }
    };

    const toggleSubmenu = (itemIndex) => {
      if (!isDesktop.value) {
        isMobileActive.value = true;
        isMobileCollapsed.value = false;
      }
      
      // Toggle the active state for this item
      activeStates[itemIndex] = !activeStates[itemIndex];
      
      // Close other items
      Object.keys(activeStates).forEach(key => {
        if (parseInt(key) !== itemIndex) {
          activeStates[key] = false;
        }
      });
    };

    const toggleNestedSubmenu = (itemIndex, subIndex) => {
      // Toggle the subactive state for this subitem
      const key = `${itemIndex}-${subIndex}`;
      subActiveStates[key] = !subActiveStates[key];
    };

    const toggleMobileSidebar = () => {
      isMobileActive.value = !isMobileActive.value;
    };

    const closeMobileSidebar = () => {
      isMobileActive.value = false;
      isMobileCollapsed.value = true;
    };

    const handleResize = () => {
      isMobileCollapsed.value = window.innerWidth < 1024;
    };

    // Initialize active states based on current route
    const initializeActiveStates = () => {
      const currentPath = route.path;
      const isAdmin = user.value?.isAdmin;
      const isBusiness = user.value?.isBusiness;
      
      // Reset all active states
      Object.keys(activeStates).forEach(key => {
        activeStates[key] = false;
      });
      
      Object.keys(subActiveStates).forEach(key => {
        subActiveStates[key] = false;
      });
      
      // Set active states based on current route
      filteredMenuItems.value.forEach((item, itemIndex) => {
        if (!item.submenu && item.link === currentPath) {
          activeStates[itemIndex] = true;
          return;
        }
        
        if (item.submenu) {
          item.submenu.forEach((subItem, subIndex) => {
            if (subItem.link === currentPath) {
              activeStates[itemIndex] = true;
              return;
            }
            
            if (subItem.submenu) {
              subItem.submenu.forEach((nestedItem) => {
                if (nestedItem.link === currentPath) {
                  activeStates[itemIndex] = true;
                  subActiveStates[`${itemIndex}-${subIndex}`] = true;
                }
              });
            }
          });
        }
        
        // Special handling for Home
        if (item.name === 'Home') {
          const isDashboardPath = currentPath === '/dashboard' || currentPath === '/dashboard/admin-dashboard';
          
          if (isAdmin && isBusiness && isDashboardPath) {
            activeStates[itemIndex] = true;
          }
        }
      });
    };

    // Add computed property for showing the button
    const showConnectShopify = computed(() => {
      // TODO: Replace with real logic to check if user is business and not already connected
      return user.value?.isBusiness && !user.value?.shopifyConnected;
    });
    // Modal logic moved to methods below
    const showModal = ref(false);
    const shopDomainInput = ref('');
    const errorMsg = ref('');

    const openShopifyModal = () => {
      showModal.value = true;
      shopDomainInput.value = '';
      errorMsg.value = '';
    };

    const closeShopifyModal = () => {
      showModal.value = false;
      errorMsg.value = '';
    };

    const submitShopifyConnect = () => {
      if (!shopDomainInput.value.endsWith('.myshopify.com')) {
        errorMsg.value = 'Please enter a valid Shopify store URL.';
        return;
      }
      // Get business_id from localStorage
      let businessId = 'BUSINESS_ID';
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.business_id) businessId = user.business_id;
      } catch (e) {
        // Could not parse user from localStorage; fallback to default
      }
      // For unpublished apps, we need to use the direct OAuth URL approach
      const shop = shopDomainInput.value;
      const clientId = '5b6153ac4184883e33ba6a9b6f423e5e';
      const redirectUri = encodeURIComponent('https://starkshopper.com/api/shopify/auth/callback');
      const scope = 'write_gift_cards';
      
      // This is the correct URL format for unpublished app installation
      const installUrl = `https://${shop}/admin/oauth/authorize?client_id=${clientId}&scope=${scope}&redirect_uri=${redirectUri}&state=${businessId}`;
      
      window.location.href = installUrl;
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      handleResize();
      initializeActiveStates();
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    watch([route, user], () => {
      initializeActiveStates();
    });

    return {
      user,
      isMobileActive,
      isMobileCollapsed,
      isDesktop,
      filteredMenuItems,
      activeStates,
      subActiveStates,
      toggleSubmenu,
      toggleNestedSubmenu,
      activateMenuItem,
      activateSubMenuItem,
      activateNestedSubMenuItem,
      toggleMobileSidebar,
      closeMobileSidebar,
      isSubMenuActive,
      isNestedSubMenuActive,
      showConnectShopify,
      showModal,
      shopDomainInput,
      errorMsg,
      openShopifyModal,
      closeShopifyModal,
      submitShopifyConnect,
    };
  }
};
</script>

<style scoped>
/* General Styles */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 250px;
  height: 100vh; /* Full viewport height */
  display: flex;
  flex-direction: column;
  background-color: #FFF;
  padding: 20px 0;
  padding-left: 10px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  transition: transform 0.3s ease, width 0.3s ease;
  justify-content: space-between; /* Ensure the version number is at the bottom */
}

.sidebar-content {
  overflow-y: auto;
  flex-grow: 1; /* Allow the content to grow and fill the remaining space */
  padding-right: 10px;
  padding-bottom: 50px; /* Avoid scrollbar overlay on content */
}

/* Custom scrollbar styling */
.sidebar-content::-webkit-scrollbar {
  width: 8px;
}

.sidebar-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.sidebar-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.sidebar-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.sidebar-header {
  padding: 20px;
  border-bottom: 1px solid #eaeaea;
  flex-shrink: 0; /* Prevents shrinking when sidebar height is reduced */
}

.sidebar-header h1 {
  color: #000;
  font-size: 24px;
  margin: 0 0 20px 0;
  padding: 0;
}

.logo {
  max-width: 100%;
  height: auto;
}

.sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.menu-item {
  margin-bottom: 10px;
}

.menu-container {
  position: relative;
  width: 100%;
}

.menu-item a, .menu-item router-link {
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  text-decoration: none;
  color: #000;
  transition: background-color 0.3s;
  overflow: hidden; /* Prevent content overflow */
  text-overflow: ellipsis; /* Add ellipsis for overflowing text */
  white-space: nowrap; /* Keep text on a single line */
}

.menu-item a.active {
  background-color: #A3BF8F;
  color: white;
}

.menu-item a:hover {
  background-color: #6E9F49;
  cursor: pointer;
}

.menu-item img {
  margin-right: 10px;
  width: 24px;
  height: 24px;
}

.menu-text {
  flex: 1;
  padding-right: 30px; /* Make room for the indicator */
}

.submenu-indicator {
  margin-left: 10px;
  width: 20px;
  height: 20px;
  transition: transform 0.3s ease;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}

.submenu-indicator.rotate {
  transform: translateY(-50%) rotate(180deg);
}

.submenu {
  list-style: none;
  padding: 0;
  margin-left: 10px;
  overflow: visible; /* Ensure submenus are visible */
}

.submenu a {
  color: #666;
  padding: 5px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.submenu a.active {
  background-color: #A3BF8F;
}

.submenu a:hover{
  background-color: #6E9F49;
  color: white;
}

.nested-submenu {
  list-style: none;
  padding: 0;
  margin-left: 15px;
}

.nested-submenu a {
  color: #777;
  padding: 5px 20px 5px 30px;
  display: block;
  font-size: 0.95em;
}

.nested-submenu-indicator {
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
}

.nested-submenu-indicator.rotate {
  transform: rotate(180deg);
}

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* Mobile Styles */
.hamburger-menu {
  display: none;
  position: fixed;
  top: 20px;
  left: 20px;
  z-index: 1001;
  cursor: pointer;
}

.hamburger-menu img {
  width: 24px;
  height: 24px;
}


.version-number {
  text-align: center;
  padding: 10px;
  font-size: 14px;
  color: #888;
  flex-shrink: 0; /* Prevents shrinking when sidebar height is reduced */
  margin-bottom: 10%;
}

@media (max-width: 1024px) {
  .sidebar {
    transform: translateX(-300px);
  }

  .sidebar.mobile-active {
    transform: translateX(0);
  }

  .hamburger-menu {
    display: block;
  }

  .menu-item a span {
    display: none;
  }

  .submenu.show-submenu {
    display: block;
  }

  .sidebar.mobile-active .menu-item a span {
    display: inline;
  }
}

@media (max-width: 768px) {
  .sidebar {
    width: 225px;
    padding-top: 75px;
  }

  .sidebar-header h1 {
    font-size: 18px;
  }

  .menu-item a span {
    display: none;
  }

  .sidebar.mobile-active .menu-item a span {
    display: inline;
  }

  .sidebar-content {
    padding-bottom: 100px; /* Avoid scrollbar overlay on content */
  }
  .version-number {
    margin-bottom: 40% !important;
  }
}

.connect-shopify-btn {
  display: block;
  width: 100%;
  margin: 10px 0 0 0;
  padding: 10px;
  background-color: #96bf48;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
}
.connect-shopify-btn:hover {
  background-color: #6e9f49;
}
.shopify-modal-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.4);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.shopify-modal {
  background: #fff;
  padding: 30px 20px 20px 20px;
  border-radius: 8px;
  box-shadow: 0 2px 16px rgba(0,0,0,0.2);
  min-width: 320px;
  max-width: 90vw;
  text-align: center;
}
.shopify-input {
  width: 90%;
  padding: 10px;
  margin: 15px 0 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.shopify-error {
  color: #e53e3e;
  margin-bottom: 10px;
}
.shopify-modal-actions {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.shopify-modal-actions button {
  padding: 8px 18px;
  border: none;
  border-radius: 4px;
  background: #96bf48;
  color: #fff;
  font-weight: bold;
  cursor: pointer;
  transition: background 0.2s;
}
.shopify-modal-actions .cancel-btn {
  background: #ccc;
  color: #333;
}
.shopify-modal-actions button:hover:not(.cancel-btn) {
  background: #6e9f49;
}
</style>